import { useGlobal } from "reactn";
import classNames from "classnames";
import { Rune } from "@outplayed/tooltips";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { RunesTableInfo } from "@ugg/shared/api/data-parser/champions/runes-table";
import { round } from "@ugg/shared/utils/math";

interface RuneStatProps {
  className?: string;
  runeId: number;
  keystone?: boolean;
  data: RunesTableInfo["keystone" | "primary" | "secondary"][number];
  patch: string;
  ssr?: boolean;
}

function RuneStat(props: RuneStatProps) {
  const { className, runeId, keystone, data, patch, ssr } = props;

  const winRate = data ? `${round(data.win_rate, 2)}%` : "-";
  const pickRate = data ? `${round(data.pick_rate, 2)}%` : "-";
  const grayscale = !data || data?.pick_rate < 1.0;
  const topPickRate = data?.pick_rate >= 50;

  return (
    <div className={classNames("flex flex-col items-center text-[11px]", className)}>
      <div
        className={classNames({
          "w-[36px] h-[36px]": keystone,
          "w-[24px] h-[24px]": !keystone,
          "grayscale-0 opacity-30": grayscale,
        })}
      >
        <Rune runeId={runeId} patch={patch} ssr={ssr} />
      </div>
      <div className={classNames("mt-[6px] font-bold text-white", { "grayscale-0 opacity-30": grayscale })}>{winRate}</div>
      <div
        className={classNames("font-medium text-lavender-50", {
          "volxd-tier": topPickRate,
          "grayscale-0 opacity-30": grayscale,
        })}
      >
        {pickRate}
      </div>
    </div>
  );
}

interface RunePathProps {
  data: RunesTableInfo;
  pathId: number;
  primary?: boolean;
  patch: string;
  ssr?: boolean;
}

function RunePath(props: RunePathProps) {
  const { useRiotRunes } = getRiotAssetsContext();
  const [language] = useGlobal("language");
  const { data, pathId, primary, patch, ssr } = props;
  const { data: runesList, loading, error } = useRiotRunes({ patch, ssr, language });

  const path = runesList?.find((path) => path.id === pathId);
  if (!path) {
    return null;
  }

  const { name, slots } = path;
  const [keystoneSlots, ...otherSlots] = slots;

  return (
    <div className="p-[12px] bg-purple-300">
      <HeaderBullet className="mb-[18px] leading-[20px]">
        <h2 className="font-bold">{name}</h2>
        <span className="ml-[12px] text-lavender-300 font-medium">{primary ? "Primary" : "Secondary"}</span>
      </HeaderBullet>
      <div>
        {primary && (
          <div className="flex items-center justify-around mb-[18px] pb-[18px] border-b-[1px] border-accent-purple-700">
            {keystoneSlots.runes.map((keystone) => (
              <RuneStat
                key={keystone.id}
                className="flex-[0_0_25%]"
                runeId={keystone.id}
                keystone
                data={data.keystone[keystone.id]}
                patch={patch}
                ssr={ssr}
              />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-[18px]">
          {otherSlots.map((row, index) => (
            <div key={index} className="flex items-center justify-around">
              {row.runes.map((rune) => (
                <RuneStat
                  key={rune.id}
                  className="flex-[0_0_25%]"
                  runeId={rune.id}
                  data={primary ? data.primary[rune.id] : data.secondary[rune.id]}
                  patch={patch}
                  ssr={ssr}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface RunesTableProps {
  className?: string;
  data: RunesTableInfo;
  patch: string;
}

export function RunesTable(props: RunesTableProps) {
  const { className, data, patch } = props;
  const pathOrder = [8000, 8100, 8200, 8400, 8300];

  return (
    <div className={classNames(className)}>
      <div className="grid gap-[1px] grid-flow-col grid-cols-[repeat(5,1fr)] w-full rounded-[3px] bg-purple-500 overflow-hidden">
        {pathOrder.map((pathId) => (
          <RunePath key={pathId} data={data} pathId={pathId} patch={patch} primary ssr />
        ))}
      </div>
      <div className="grid gap-[1px] grid-flow-col grid-cols-[repeat(5,1fr)] mt-[12px] w-full rounded-[3px] bg-purple-500 overflow-hidden">
        {pathOrder.map((pathId) => (
          <RunePath key={pathId} data={data} pathId={pathId} patch={patch} ssr />
        ))}
      </div>
    </div>
  );
}
